<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { PrimaryButton, SecondaryButton, useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunityCompaniesStore } from '@/stores/communityCompaniesStore';
import { useCommunityProviderStore } from '@/stores/communityProviderStore';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import ServiceFeesTab from '@/components/service-fees/ServiceFeesTab.vue';
import TariffServiceFeeMatrix from '@/components/service-fees/TariffServiceFeeMatrix.vue';
import { matrixRowsForCompany } from '@/utils/tariffServiceFeeMatrixUtils.js';
import dateUtil from '@/lib/es-bill-shared-js/util/DateUtil.js';

const $log = inject('log');
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const snackbarStore = useSnackbarStore();
const companiesStore = useCommunityCompaniesStore();
const providerStore = useCommunityProviderStore();
const communitiesStore = useCommunitiesStore();

const ecn = computed(() => route.params.ecn);

const tab = ref('communities');
const form = ref(null);

async function loadData() {
  if (!companiesStore.allCommunityCompanies.length)
    await companiesStore.fetchCommunityCompanies().catch(() => {});
  if (!providerStore.allProviders.length)
    await providerStore.fetchProviders().catch(() => {});
  if (!communitiesStore.allCommunities.length)
    await communitiesStore.fetchCommunities().catch(() => {});

  const company = companiesStore.getCommunityCompany(ecn.value);
  if (company && company.ecn === ecn.value)
    form.value = JSON.parse(JSON.stringify(company));
}
loadData();
watch(ecn, loadData);

const assignedCommunities = computed(() =>
  communitiesStore.allCommunities.filter(c => c.ecn === ecn.value));

const communitiesHeader = computed(() => [
  { title: 'ECID', value: 'ecid' },
  { title: t('name'), value: 'name' },
  { title: t('communityCompanies.communityType'), value: 'communityType' },
  { title: t('serviceFees.dateStart'), value: 'dateStartDisplay' },
  { title: t('serviceFees.dateEnd'), value: 'dateEndDisplay' },
]);

function openCommunity(event, { item }) {
  router.push({ name: 'communityDetailForm', params: { ecid: item.ecid } });
}

const communityRows = computed(() => assignedCommunities.value.map(c => ({
  ...c,
  dateStartDisplay: c.dateStart ? dateUtil.formatDate(c.dateStart) : '',
  dateEndDisplay: c.dateEnd ? dateUtil.formatDate(c.dateEnd) : '',
})));

// provider assignment state (admin tab, dedicated backend calls)
const currentProviderId = computed(() => {
  const providerRef = form.value?.communityProvider;
  if (!providerRef) return null;
  return typeof providerRef === 'object' ? providerRef._id : providerRef;
});

const currentProviderName = computed(() => {
  if (!currentProviderId.value) return '';
  const provider = providerStore.providerById.get(currentProviderId.value);
  return provider ? provider.nameFriendly : currentProviderId.value;
});

const selectedProviderId = ref(null);

const providerOptions = computed(() => providerStore.allProviders.map(p => ({
  value: p._id,
  title: p.nameFriendly,
})));

// CC matrix: provider resolved from the store since the metadata call does
// not populate the reference
const matrixRows = computed(() => matrixRowsForCompany(
  form.value,
  assignedCommunities.value,
  currentProviderId.value ? providerStore.providerById.get(currentProviderId.value) : null,
));

async function refreshCompany() {
  await companiesStore.fetchCommunityCompanies().catch(() => {});
  const company = companiesStore.getCommunityCompany(ecn.value);
  if (company && company.ecn === ecn.value)
    form.value = JSON.parse(JSON.stringify(company));
}

async function onAssignProvider() {
  if (!selectedProviderId.value) return;
  try {
    await providerStore.assignCompany(selectedProviderId.value, ecn.value);
    await refreshCompany();
    selectedProviderId.value = null;
    snackbarStore.showSnackbar(t('communityCompanies.assignSuccess'), '', 'Success', 4000, []);
  } catch (error) {
    $log?.warn('assign communityProvider failed', error);
    snackbarStore.showSnackbar(t('connectionProblem'), '', 'Error', 10000, []);
  }
}

async function onUnassignProvider() {
  try {
    await providerStore.unassignCompany(ecn.value);
    await refreshCompany();
    snackbarStore.showSnackbar(t('communityCompanies.unassignSuccess'), '', 'Success', 4000, []);
  } catch (error) {
    $log?.warn('unassign communityProvider failed', error);
    snackbarStore.showSnackbar(t('connectionProblem'), '', 'Error', 10000, []);
  }
}

async function onSave() {
  try {
    const updated = await companiesStore.updateCommunityCompany(ecn.value, form.value);
    form.value = JSON.parse(JSON.stringify(updated));
    snackbarStore.showSnackbar(t('saved'), '', 'Success', 4000, []);
  } catch (error) {
    $log?.warn('save communityCompany failed', error);
    if (error?.response?.status === 409)
      snackbarStore.showSnackbar(t('communityCompanies.versionConflict'), '', 'Error', 10000, []);
    else
      snackbarStore.showSnackbar(t('connectionProblem'), '', 'Error', 10000, []);
  }
}

function onServiceFeesUpdate(fees) {
  form.value.serviceFees = fees;
  onSave();
}
</script>

<template>
  <v-container v-if="form">
    <div class="d-flex justify-space-between align-center mb-4">
      <p class="textHeadline">{{ form.name }} ({{ form.ecn }})</p>
      <PrimaryButton :text="t('save')" @click="onSave" />
    </div>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab value="communities">{{ t('communityCompanies.tabCommunities') }}</v-tab>
      <v-tab value="serviceFees">{{ t('serviceFees.title') }}</v-tab>
      <v-tab value="matrix">{{ t('matrix.title') }}</v-tab>
      <v-tab value="masterData">{{ t('communityCompanies.tabMasterData') }}</v-tab>
      <v-tab value="admin">{{ t('communityCompanies.tabAdmin') }}</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="communities">
        <v-data-table
          class="elevation-2 mainViewContent"
          :headers="communitiesHeader"
          :items="communityRows"
          :items-per-page="25"
          density="comfortable"
          @click:row="openCommunity"
        />
      </v-window-item>

      <v-window-item value="serviceFees">
        <ServiceFeesTab
          :service-fees="form.serviceFees || []"
          @update:service-fees="onServiceFeesUpdate"
        />
      </v-window-item>

      <v-window-item value="matrix">
        <TariffServiceFeeMatrix :rows="matrixRows" />
      </v-window-item>

      <v-window-item value="masterData" class="pt-2">
        <v-row dense>
          <v-col cols="6">
            <v-text-field :model-value="form.ecn" label="ECN" bg-color="white" variant="outlined" density="compact" readonly disabled />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.name" :label="t('name')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.legal" :label="t('communityProviders.legal')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.accountingReference" :label="t('communityProviders.accountingReference')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-textarea v-model="form.notes" :label="t('communityProviders.notes')" bg-color="white" variant="outlined" density="compact" rows="3" />
      </v-window-item>

      <v-window-item value="admin" class="pt-2">
        <p class="text-subtitle-2 mb-2">{{ t('communityCompanies.providerAssignment') }}</p>
        <p class="text-body-2 mb-4">
          {{ currentProviderId
            ? t('communityCompanies.communityProvider') + ': ' + currentProviderName
            : t('communityCompanies.noProvider') }}
        </p>
        <v-row dense align="center">
          <v-col cols="6">
            <v-select
              v-model="selectedProviderId"
              :items="providerOptions"
              :label="t('communityCompanies.selectProvider')"
              bg-color="white" variant="outlined" density="compact"
              clearable
            />
          </v-col>
          <v-col cols="6">
            <PrimaryButton
              class="mr-2"
              :text="t('communityCompanies.assignProvider')"
              :disabled="!selectedProviderId"
              @click="onAssignProvider"
            />
            <SecondaryButton
              v-if="currentProviderId"
              :text="t('communityCompanies.unassignProvider')"
              @click="onUnassignProvider"
            />
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>
  </v-container>
</template>
